import { score } from '@alison/ui';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import { alpha, keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Fragment, type ReactNode, useState } from 'react';
import miniArrow from '../../../../assets/alison-run/mini-arrow.svg';
import miniCheck from '../../../../assets/alison-run/mini-check.svg';
import openDrawerIcon from '../../../../assets/alison-run/open-drawer.svg';
import outcomeBrief from '../../../../assets/alison-run/outcome-brief.svg';
import outcomeReport from '../../../../assets/alison-run/outcome-report.svg';
import outcomeStory from '../../../../assets/alison-run/outcome-story.svg';
import stepCheck from '../../../../assets/alison-run/step-check.svg';
import stepPending from '../../../../assets/alison-run/step-pending.svg';
import { type FieldValues, missingRequired } from '../../fieldValues.js';
import { buildRunParams, openingValues } from '../../runParams.js';
import type { MapOutcome } from '../../sessionRows.js';
import { useAlisonRun } from '../AlisonRunProvider.js';
import { changedParams } from '../data/alisonLive.js';
import {
  type AlisonRow,
  builtRows,
  fillTemplate,
  isFinished,
  stageRows,
  stepProgress,
} from '../data/alisonRun.js';
import { DrawerFooter, DrawerHeader, GradientButton } from './AlisonDrawerChrome.js';
import { AlisonPathBuilder, StepConfiguration } from './AlisonPathBuilder.js';
import {
  ALISON_PROGRESS_GRADIENT,
  ALISON_SELECTED,
  ALISON_TEXT_DISABLED,
  ALISON_TEXT_SECONDARY,
  ALISON_TILE_GRADIENTS,
} from './alisonRunColors.js';

export const ALISON_DRAWER_WIDTH = { xs: '100%', md: '820px' };

const SEPARATOR = '\u00b7';
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  top: 0,
  left: 0,
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
const TILES = {
  report: {
    icon: outcomeReport,
    background: ALISON_TILE_GRADIENTS.report,
    iconSx: { left: '25%', top: '21.88%', width: 16, height: 18 },
  },
  storyboards: {
    icon: outcomeStory,
    background: ALISON_TILE_GRADIENTS.storyboards,
    iconSx: { left: '25%', top: '28.13%', width: 16.25, height: 13.75 },
  },
  brief: {
    icon: outcomeBrief,
    background: ALISON_TILE_GRADIENTS.brief,
    iconSx: { left: 8, top: 7, width: 20, height: 22 },
  },
} as const;
const activePulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;
const PULSE_SX = {
  animation: `${activePulse} 1.6s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

type TileKind = keyof typeof TILES;

interface Stage {
  row: AlisonRow;
  // The auxiliary steps run on the way to `row`, shown as its substeps.
  supports: AlisonRow[];
}

export function AlisonRunDrawer(): ReactNode {
  const { drawerOpen, session, path, copy, closeDrawer } = useAlisonRun();
  const root = path?.[0];
  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={closeDrawer}
      hideBackdrop
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-label': copy.title,
          sx: { width: ALISON_DRAWER_WIDTH, bgcolor: 'background.default' },
        },
      }}
    >
      {session !== null ? (
        <RunView />
      ) : root !== undefined ? (
        <AlisonPathBuilder key={root} root={root} />
      ) : null}
    </Drawer>
  );
}

function stagesOf(rows: AlisonRow[]): Stage[] {
  const stages: Stage[] = [];
  let supports: AlisonRow[] = [];
  for (const row of rows) {
    if (row.auxiliary) {
      supports.push(row);
      continue;
    }
    stages.push({ row, supports });
    supports = [];
  }
  return [...stages, ...supports.map((row) => ({ row, supports: [] }))];
}

function PhaseStrip({ rows }: { rows: AlisonRow[] }): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
      {stageRows(rows).map((row, index) => (
        <Fragment key={row.actionType}>
          {index > 0 && (
            <Typography sx={{ fontSize: 10, lineHeight: 'normal', color: ALISON_TEXT_DISABLED }}>
              {SEPARATOR}
            </Typography>
          )}
          <PhaseChip row={row} />
        </Fragment>
      ))}
    </Stack>
  );
}

function PhaseChip({ row }: { row: AlisonRow }): ReactNode {
  if (row.status === 'pending') {
    return (
      <Typography
        sx={{ fontSize: 11, fontWeight: 400, lineHeight: 'normal', color: ALISON_TEXT_DISABLED }}
      >
        {row.title}
      </Typography>
    );
  }
  const active = row.status === 'active';
  const failed = row.status === 'failed';
  return (
    <Stack
      direction="row"
      sx={{
        gap: '4px',
        alignItems: 'center',
        fontWeight: 600,
        lineHeight: 'normal',
        ...(failed && { color: 'error.main' }),
        ...(active && {
          pl: '8px',
          pr: '9px',
          py: '3px',
          borderRadius: '999px',
          color: 'primary.main',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
        }),
      }}
    >
      <Box
        component="span"
        sx={{ fontSize: active ? 7 : 9, color: active || failed ? 'inherit' : 'success.dark' }}
      >
        {active ? '●' : failed ? '✕' : '✓'}
      </Box>
      <Box
        component="span"
        sx={{ fontSize: 11, color: active || failed ? 'inherit' : ALISON_TEXT_SECONDARY }}
      >
        {row.title}
      </Box>
    </Stack>
  );
}

function RunView(): ReactNode {
  const { rows, session, copy, stop, closeDrawer, openResults, editingActionType } = useAlisonRun();
  const finished = isFinished(session);
  const failed = session?.status === 'failed';
  const { current, total } = stepProgress(rows);
  const activeRow = rows.find((row) => row.status === 'active');
  const built = builtRows(rows);
  if (editingActionType !== null) {
    return <EditStepView key={editingActionType} actionType={editingActionType} />;
  }
  const failedHeadline = fillTemplate(copy.failedPill, {
    title: rows.find((row) => row.status !== 'done')?.title ?? '',
  });
  const label = finished
    ? fillTemplate(copy.doneLabel, { total: built.length })
    : failed
      ? failedHeadline
      : fillTemplate(copy.progressLabel, { current, total, title: activeRow?.runningTitle ?? '' });
  const doneCount = builtRows(stageRows(rows)).length;

  return (
    <Stack sx={{ height: '100%' }}>
      <DrawerHeader
        subtitle={fillTemplate(copy.runningSubtitle, {
          stages: rows.map((row) => row.title).join(', '),
        })}
        height={100}
        running
      />
      <Stack sx={{ flex: 1, minHeight: 0, gap: '12px', px: '24px', py: '16px', overflowY: 'auto' }}>
        <Box
          sx={{
            alignSelf: 'flex-start',
            px: '14px',
            py: '8px',
            borderRadius: '0 12px 12px 12px',
            bgcolor: ALISON_SELECTED,
          }}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, lineHeight: 'normal', color: 'background.icon' }}
          >
            {rows[rows.length - 1]?.title ?? ''}
          </Typography>
        </Box>
        <Stack sx={{ position: 'relative', gap: '8px', flexShrink: 0 }}>
          <Box component="span" sx={VISUALLY_HIDDEN}>
            {label}
          </Box>
          <PhaseStrip rows={rows} />
          <Box
            sx={{
              height: 6,
              borderRadius: '999px',
              overflow: 'hidden',
              bgcolor: 'divider',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${(doneCount / total) * 100}%`,
                borderRadius: '999px',
                background: ALISON_PROGRESS_GRADIENT,
              }}
            />
          </Box>
        </Stack>
        {stagesOf(rows).map((stage, index) => (
          <StepRow
            key={stage.row.actionType}
            row={stage.row}
            supports={stage.supports}
            index={index + 1}
          />
        ))}
        {finished && (
          <EndCard
            headline={fillTemplate(copy.doneHeadline, {
              outcome: built[built.length - 1]?.title ?? '',
            })}
            body={copy.doneBody}
          />
        )}
        {failed && <EndCard headline={failedHeadline} body={session.failure} />}
      </Stack>
      <DrawerFooter note={finished ? copy.runComplete : failed ? '' : copy.footnote}>
        {failed ? (
          <TextAction onClick={closeDrawer}>{copy.close}</TextAction>
        ) : finished ? (
          <>
            <TextAction onClick={closeDrawer}>{copy.close}</TextAction>
            <GradientButton onClick={openResults}>
              {fillTemplate(copy.viewResults, {
                outcome: built[built.length - 1]?.title ?? '',
              })}
            </GradientButton>
          </>
        ) : (
          <>
            <OutlinedAction onClick={stop}>{copy.stop}</OutlinedAction>
            <OutlinedAction onClick={closeDrawer}>{copy.runInBackground}</OutlinedAction>
          </>
        )}
      </DrawerFooter>
    </Stack>
  );
}

function EndCard({ headline, body }: { headline: string; body: string | null }): ReactNode {
  return (
    <Stack
      sx={{
        gap: '6px',
        p: '14px',
        borderRadius: '10px',
        bgcolor: 'background.platform',
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{ fontSize: 13, fontWeight: 600, lineHeight: 'normal', color: 'background.icon' }}
      >
        {headline}
      </Typography>
      {body !== null && (
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 400,
            lineHeight: 'normal',
            color: ALISON_TEXT_SECONDARY,
          }}
        >
          {body}
        </Typography>
      )}
    </Stack>
  );
}

function OutlinedAction({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <Button
      variant="outlined"
      onClick={onClick}
      sx={{
        minWidth: 0,
        px: '11px',
        py: '7px',
        borderRadius: '9px',
        border: 1,
        borderColor: 'text.muted',
        color: 'background.icon',
        bgcolor: 'background.paper',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 'normal',
        '&:hover': { borderColor: 'text.muted', bgcolor: 'background.paper' },
      }}
    >
      {children}
    </Button>
  );
}

function TextAction({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <Button
      onClick={onClick}
      {...(label === undefined ? {} : { 'aria-label': label })}
      sx={{
        minWidth: 0,
        px: '10px',
        py: '8px',
        borderRadius: '9px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 'normal',
        color: 'primary.main',
      }}
    >
      {children}
    </Button>
  );
}

function MiniLine({
  icon,
  size,
  children,
}: {
  icon: string;
  size: number;
  children: string;
}): ReactNode {
  return (
    <Stack direction="row" sx={{ gap: '6px', alignItems: 'flex-start' }}>
      <Box component="img" src={icon} alt="" sx={{ width: size, height: size, flexShrink: 0 }} />
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 11,
          fontWeight: 400,
          lineHeight: 'normal',
          color: ALISON_TEXT_SECONDARY,
        }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

function StepRow({
  row,
  supports,
  index,
}: {
  row: AlisonRow;
  supports: AlisonRow[];
  index: number;
}): ReactNode {
  const { copy, peek, editStep, setup, details } = useAlisonRun();
  const active = row.status === 'active';
  const editable =
    !active &&
    setup !== undefined &&
    (details
      .find((detail) => detail.workflowId === row.actionType)
      ?.steps.find((step) => step.kind === 'config')?.fields.length ?? 0) > 0;
  const substeps = [
    ...supports.map((support) => ({
      key: support.actionType,
      done: support.status === 'done',
      text:
        support.status === 'active'
          ? (support.progress ?? support.runningTitle)
          : (support.summary ?? support.title),
      current: support.status === 'active',
    })),
    ...(row.status === 'done' && row.summary !== null
      ? [{ key: 'summary', done: true, text: row.summary, current: false }]
      : []),
    ...(active && row.progress !== null
      ? [{ key: 'progress', done: false, text: row.progress, current: true }]
      : []),
    ...(active && row.progress === null
      ? row.runningSteps.map((line) => ({ key: line, done: false, text: line, current: false }))
      : []),
  ].filter((substep) => substep.text !== '');
  return (
    <Fragment>
      <Stack direction="row" sx={{ gap: '10px', alignItems: 'flex-start', flexShrink: 0 }}>
        <StatusIcon status={row.status} index={index} />
        <Stack sx={{ flex: 1, minWidth: 0, gap: '3px' }}>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              lineHeight: 'normal',
              color: 'background.icon',
              ...(active && PULSE_SX),
            }}
          >
            {active ? row.runningTitle || row.title : row.title}
          </Typography>
          {substeps.length > 0 && (
            <Stack sx={{ gap: '3px', pt: '2px' }}>
              {substeps.map((substep) => (
                <Box key={substep.key} sx={substep.current ? PULSE_SX : undefined}>
                  <MiniLine
                    icon={substep.done ? miniCheck : miniArrow}
                    size={substep.done ? 13 : 11}
                  >
                    {substep.text}
                  </MiniLine>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
        {editable && (
          <TextAction
            onClick={() => editStep(row.actionType)}
            label={fillTemplate(copy.editStepLabel, { title: row.title })}
          >
            {copy.editStep}
          </TextAction>
        )}
        {row.status === 'done' && row.outcome !== null && (
          <TextAction onClick={() => peek(row.actionType)}>{copy.view}</TextAction>
        )}
      </Stack>
      {[...supports, row].map(
        (shown) =>
          shown.alisonCall !== null &&
          shown.status !== 'pending' && (
            <Stack
              key={`call-${shown.actionType}`}
              direction="row"
              sx={{
                ml: '34px',
                gap: '8px',
                alignItems: 'center',
                px: '10px',
                py: '8px',
                borderRadius: '8px',
                color: 'score.medium.text',
                bgcolor: alpha(score.medium.text, 0.1),
                lineHeight: 'normal',
                flexShrink: 0,
              }}
            >
              <Box component="span" sx={{ fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {copy.alisonCallLabel}
              </Box>
              <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 400 }}>
                {shown.alisonCall}
              </Box>
            </Stack>
          ),
      )}
      {[...supports, row].map(
        (shown) =>
          shown.outcome !== null && (
            <Box key={`outcome-${shown.actionType}`} sx={{ pl: '34px', flexShrink: 0 }}>
              <DrawerOutcomeCard outcome={shown.outcome} onOpen={() => peek(shown.actionType)} />
            </Box>
          ),
      )}
    </Fragment>
  );
}

function tileKind(kind: string): TileKind {
  switch (kind) {
    case 'report':
      return 'report';
    case 'storyboards':
      return 'storyboards';
    case 'brief':
    case 'video':
      return 'brief';
    default:
      return 'brief';
  }
}

export function DrawerOutcomeCard({
  outcome,
  onOpen,
  fill = false,
}: {
  outcome: MapOutcome;
  onOpen: () => void;
  fill?: boolean;
}): ReactNode {
  const tile = TILES[tileKind(outcome.kind)];
  return (
    <ButtonBase
      onClick={onOpen}
      data-testid={`outcome-card-${outcome.runId}`}
      sx={{
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center',
        px: '10px',
        py: '11px',
        textAlign: 'left',
        borderRadius: '9px',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: '9px',
          overflow: 'hidden',
          background: tile.background,
        }}
      >
        <Box
          component="img"
          src={tile.icon}
          alt=""
          sx={{ position: 'absolute', maxWidth: 'none', ...tile.iconSx }}
        />
      </Box>
      <Stack sx={{ gap: '2px', ...(fill && { flex: 1, minWidth: 0 }) }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            color: 'background.icon',
          }}
        >
          {outcome.date ? `${outcome.name} ${outcome.date}` : outcome.name}
        </Typography>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            lineHeight: 'normal',
            color: 'text.muted',
            textTransform: 'capitalize',
          }}
        >
          {outcome.kind}
        </Typography>
      </Stack>
      <Box
        component="img"
        src={openDrawerIcon}
        alt=""
        sx={{ width: 16, height: 16, flexShrink: 0, ml: '8px' }}
      />
    </ButtonBase>
  );
}

function StatusIcon({ status, index }: { status: AlisonRow['status']; index: number }): ReactNode {
  if (status === 'pending') {
    return (
      <Box component="img" src={stepPending} alt="" sx={{ width: 24, height: 24, flexShrink: 0 }} />
    );
  }
  const done = status === 'done';
  const failed = status === 'failed';
  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        width: 24,
        height: 24,
        flexShrink: 0,
        boxSizing: 'border-box',
        borderRadius: '24px',
        border: '1px solid',
        borderColor: done ? 'success.dark' : failed ? 'error.main' : 'background.icon',
        bgcolor: (theme) =>
          done
            ? alpha(theme.palette.success.dark, 0.1)
            : failed
              ? theme.palette.error.main
              : theme.palette.background.icon,
        color: 'common.white',
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {done ? <Box component="img" src={stepCheck} alt="" sx={{ width: 16, height: 16 }} /> : index}
    </Box>
  );
}

// A step's settings, opened from its row: saving reruns the path from that step.
function EditStepView({ actionType }: { actionType: string }): ReactNode {
  const { copy, details, setup, rows, paramsOf, rerunFrom, editStep } = useAlisonRun();
  const step = details
    .find((detail) => detail.workflowId === actionType)
    ?.steps.find((candidate) => candidate.kind === 'config');
  const [opening] = useState<FieldValues>(() =>
    step === undefined || setup === undefined
      ? {}
      : openingValues(step.fields, paramsOf(actionType), setup.params ?? {}, setup.leadingKpi),
  );
  const [values, setValues] = useState(opening);
  const title = rows.find((row) => row.actionType === actionType)?.title ?? actionType;
  const missing = step === undefined ? [] : missingRequired(step.fields, values);
  return (
    <Stack sx={{ height: '100%' }}>
      <DrawerHeader
        subtitle={fillTemplate(copy.rerunNote, { title })}
        height={100}
        running={false}
      />
      <Stack sx={{ flex: 1, minHeight: 0, gap: '16px', px: '24px', py: '16px', overflowY: 'auto' }}>
        <Typography variant="h3" component="h2">
          {fillTemplate(copy.editStepLabel, { title })}
        </Typography>
        {step !== undefined && setup !== undefined && (
          <StepConfiguration step={step} values={values} setup={setup} onChange={setValues} />
        )}
        {missing.length > 0 && copy.missingFields !== undefined && (
          <Typography role="alert" color="error">
            {fillTemplate(copy.missingFields, { fields: missing.join(', ') })}
          </Typography>
        )}
      </Stack>
      <DrawerFooter note="">
        <TextAction onClick={() => editStep(null)}>{copy.cancel}</TextAction>
        <GradientButton
          disabled={step === undefined || missing.length > 0}
          onClick={() => {
            if (step === undefined) return;
            rerunFrom(
              actionType,
              changedParams(
                buildRunParams(step.fields, opening),
                buildRunParams(step.fields, values),
              ),
            );
          }}
        >
          {copy.rerunFrom}
        </GradientButton>
      </DrawerFooter>
    </Stack>
  );
}
